import { useState, useEffect, useCallback } from 'react';

const PANEL_WIDTH_STORAGE_KEY = 'documentationDependenciesPanelWidth';
const DEFAULT_WIDTH = 464;
const MAX_WIDTH = 850;
const MIN_WIDTH = 464;

export const usePanelWidth = () => {
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    // Initialize from localStorage or use default
    const savedWidth = localStorage.getItem(PANEL_WIDTH_STORAGE_KEY);
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      // Ensure the saved width is within bounds
      return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
    }
    return DEFAULT_WIDTH;
  });

  const updatePanelWidth = useCallback((newWidth: number) => {
    // Constrain the width within bounds
    const constrainedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
    setPanelWidth(constrainedWidth);

    // Save to localStorage
    localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, constrainedWidth.toString());
  }, []);

  // Listen for storage events (in case multiple tabs are open)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PANEL_WIDTH_STORAGE_KEY && e.newValue) {
        const width = parseInt(e.newValue, 10);
        if (!isNaN(width)) {
          setPanelWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width)));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    panelWidth,
    updatePanelWidth,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
  };
};
